<template>
  <div
    ref="root"
    class="sb-global-dropdown"
    :style="rootStyles"
    :class="[
      {
        'is-open': isOpen,
        'is-disabled': disabled,
        'is-full-width': fullWidth,
      },
      triggerVariantClass,
    ]"
  >
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      class="sb-global-dropdown__trigger"
      :class="[sizeClass, triggerVariantClass, triggerClass]"
      :disabled="isTriggerDisabled"
      :aria-expanded="isOpen.toString()"
      :aria-controls="`${dropdownId}-panel`"
      aria-haspopup="listbox"
      :aria-activedescendant="activeDescendant"
      @click.stop="toggleDropdown"
      @keydown="onTriggerKeydown"
    >
      <span class="sb-global-dropdown__label">
        {{ displayLabel }}
      </span>
      <span class="sb-global-dropdown__chevron" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline
            points="6 9 12 15 18 9"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </button>

    <ul
      v-show="isOpen"
      :id="`${dropdownId}-panel`"
      ref="panel"
      class="sb-global-dropdown__panel"
      :class="panelClass"
      :style="mergedPanelStyles"
      role="listbox"
      tabindex="-1"
      @mousedown.stop
      @touchstart.stop
      @click.stop
      @keydown="onMenuKeydown"
    >
      <li
        v-if="!options.length"
        class="sb-global-dropdown__option is-disabled is-empty"
        role="option"
        aria-selected="false"
        aria-disabled="true"
      >
        No Data Available
      </li>
      <li
        v-for="(option, index) in options"
        v-else
        :id="getOptionId(index)"
        :key="option.id || `${dropdownId}-${index}`"
        ref="optionNodes"
        class="sb-global-dropdown__option"
        :class="{
          'is-selected': value === option.value,
          'is-focused': focusedIndex === index,
          'is-disabled': option.disabled,
        }"
        role="option"
        :aria-selected="(value === option.value).toString()"
        :aria-disabled="!!option.disabled"
        @mouseenter="onOptionMouseEnter(index)"
        @mousedown.stop.prevent
        @touchstart.stop
        @click.stop="selectOptionByIndex(index)"
      >
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>

<script>
import {
  registerOpenDropdown,
  unregisterOpenDropdown,
} from '../utility/dropdownManager.js';

const ALLOWED_SIZES = [24, 28, 32];

export default {
  name: 'GlobalDropdown',
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
    },
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    size: {
      type: [Number, String],
      default: 32,
    },
    minWidth: {
      type: [Number, String],
      default: 200,
    },
    width: {
      type: [Number, String],
      default: null,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    triggerClass: {
      type: [String, Array, Object],
      default: '',
    },
    panelClass: {
      type: [String, Array, Object],
      default: '',
    },
    triggerVariant: {
      type: String,
      default: 'default',
      validator(value) {
        return ['default', 'legacy-underline'].includes(value);
      },
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    matchTriggerWidth: {
      type: Boolean,
      default: false,
    },
    portalZIndexMax: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      uniqueId: `sb-dropdown-${this._uid}`,
      portalStyles: null,
    };
  },
  computed: {
    dropdownId() {
      return this.id || this.uniqueId;
    },
    triggerId() {
      return this.id || `${this.dropdownId}-trigger`;
    },
    normalizedSize() {
      const parsed = Number(this.size);
      return ALLOWED_SIZES.includes(parsed) ? parsed : 32;
    },
    sizeClass() {
      return `size-${this.normalizedSize}`;
    },
    triggerVariantClass() {
      return `trigger-variant-${this.triggerVariant}`;
    },
    normalizedMinWidth() {
      if (typeof this.minWidth === 'number') {
        return `${this.minWidth}px`;
      }
      if (typeof this.minWidth === 'string' && /^\d+$/.test(this.minWidth)) {
        return `${this.minWidth}px`;
      }
      return this.minWidth;
    },
    normalizedWidth() {
      if (this.width == null || this.width === '') {
        return '';
      }
      if (typeof this.width === 'number') {
        return `${this.width}px`;
      }
      if (typeof this.width === 'string' && /^\d+$/.test(this.width)) {
        return `${this.width}px`;
      }
      return this.width;
    },
    rootStyles() {
      if (!this.normalizedWidth) {
        return undefined;
      }
      return {
        width: this.normalizedWidth,
      };
    },
    selectedIndex() {
      return this.options.findIndex((option) => option.value === this.value);
    },
    selectedOption() {
      if (this.selectedIndex < 0) return null;
      return this.options[this.selectedIndex];
    },
    displayLabel() {
      if (this.selectedOption && this.selectedOption.label != null) {
        return this.selectedOption.label;
      }
      if (!this.options.length) {
        return 'No Data Available';
      }
      return this.placeholder;
    },
    isTriggerDisabled() {
      return this.disabled || !this.options.length;
    },
    panelStyles() {
      const styles = {
        minWidth: `max(${this.normalizedMinWidth}, 100%)`,
      };
      if (this.matchTriggerWidth) {
        styles.width = '100%';
      }
      return styles;
    },
    mergedPanelStyles() {
      if (!this.shouldUsePortal() || !this.portalStyles) {
        return this.panelStyles;
      }
      return {
        ...this.panelStyles,
        ...this.portalStyles,
      };
    },
    activeDescendant() {
      if (!this.isOpen || this.focusedIndex < 0) {
        return undefined;
      }
      return this.getOptionId(this.focusedIndex);
    },
  },
  beforeDestroy() {
    this.removeDocumentListeners();
    this.removePositionListeners();
    this.restorePanelToRoot();
    unregisterOpenDropdown(this.dropdownId);
  },
  methods: {
    shouldUsePortal() {
      return this.appendToBody || this.isInsideDialog();
    },
    isInsideDialog() {
      const trigger = this.$refs.trigger;
      if (!trigger || typeof trigger.closest !== 'function') {
        return false;
      }
      return !!trigger.closest('.v-dialog__content');
    },
    addDocumentListeners() {
      document.addEventListener(
        'mousedown',
        this.handleOutsidePointerDown,
        true
      );
      document.addEventListener(
        'touchstart',
        this.handleOutsidePointerDown,
        true
      );
    },
    removeDocumentListeners() {
      document.removeEventListener(
        'mousedown',
        this.handleOutsidePointerDown,
        true
      );
      document.removeEventListener(
        'touchstart',
        this.handleOutsidePointerDown,
        true
      );
    },
    getPortalHost() {
      return document.body;
    },
    getPortalZIndex() {
      const parseCssVarInt = (name, fallback) => {
        const raw = window
          .getComputedStyle(document.documentElement)
          .getPropertyValue(name)
          .trim();
        const parsed = Number.parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      // Outside dialogs, keep portal dropdowns below MainLayout topbar layers.
      if (!this.isInsideDialog()) {
        const topbarZ = parseCssVarInt('--sb-z-topbar', 980);
        const floatingZ = parseCssVarInt(
          '--sb-z-floating-content',
          topbarZ - 1
        );
        return Math.max(1, Math.min(floatingZ, topbarZ - 1));
      }

      const getZ = (element) => {
        const z = Number.parseInt(window.getComputedStyle(element).zIndex, 10);
        return Number.isFinite(z) ? z : 0;
      };
      const dialogNodes = Array.from(
        document.querySelectorAll(
          '.v-dialog__content, .v-dialog__content--active'
        )
      );
      const overlayNodes = Array.from(
        document.querySelectorAll('.v-overlay, .v-overlay--active')
      );
      const highestDialog = dialogNodes.reduce(
        (max, node) => Math.max(max, getZ(node)),
        0
      );
      const highestOverlay = overlayNodes.reduce(
        (max, node) => Math.max(max, getZ(node)),
        0
      );
      const highestLayer = Math.max(highestDialog, highestOverlay);
      return (highestLayer > 0 ? highestLayer : 1200) + 20;
    },
    getPortalZIndexMax() {
      if (this.portalZIndexMax == null || this.portalZIndexMax === '') {
        return null;
      }
      const parsed =
        typeof this.portalZIndexMax === 'number'
          ? this.portalZIndexMax
          : Number.parseInt(this.portalZIndexMax, 10);
      if (!Number.isFinite(parsed)) {
        return null;
      }
      return Math.max(1, parsed);
    },
    getPortalMinTop() {
      if (this.isInsideDialog()) return 4;
      const topbarHeight = Number.parseInt(
        window
          .getComputedStyle(document.documentElement)
          .getPropertyValue('--sb-topbar-h')
          .trim(),
        10
      );
      return (Number.isFinite(topbarHeight) ? topbarHeight : 48) + 4;
    },
    containsEventTarget(target) {
      const root = this.$refs.root;
      const panel = this.$refs.panel;
      if (root && root.contains(target)) {
        return true;
      }
      if (panel && panel.contains && panel.contains(target)) {
        return true;
      }
      return false;
    },
    getPortalMinWidth(triggerWidth) {
      const minWidthProp = this.minWidth;
      if (typeof minWidthProp === 'number') {
        return `${minWidthProp}px`;
      }
      if (typeof minWidthProp === 'string') {
        const trimmed = minWidthProp.trim();
        if (/^\d+$/.test(trimmed)) {
          return `${trimmed}px`;
        }
        if (/^\d+(\.\d+)?px$/.test(trimmed)) {
          return trimmed;
        }
        if (trimmed.endsWith('%')) {
          const percentage = parseFloat(trimmed);
          if (Number.isFinite(percentage)) {
            return `${(triggerWidth * percentage) / 100}px`;
          }
        }
      }
      return this.normalizedMinWidth;
    },
    // eslint-disable-next-line complexity
    syncPanelPosition() {
      if (!this.shouldUsePortal() || !this.isOpen) return;
      const trigger = this.$refs.trigger;
      const panel = this.$refs.panel;
      if (!trigger || !panel) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 2;
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const panelHeight = panel.offsetHeight || 0;
      const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap);
      const spaceAbove = Math.max(0, rect.top - gap);
      const openUp = panelHeight > spaceBelow && spaceAbove > spaceBelow;
      const minTop = this.getPortalMinTop();
      const top = openUp
        ? Math.max(minTop, Math.round(rect.top - gap - panelHeight))
        : Math.round(rect.bottom + gap);
      const maxHeight = Math.max(
        120,
        Math.floor((openUp ? spaceAbove : spaceBelow) - 4)
      );
      const computedZIndex = this.getPortalZIndex();
      const maxZIndex = this.getPortalZIndexMax();
      const zIndex =
        maxZIndex == null || this.isInsideDialog()
          ? computedZIndex
          : Math.min(computedZIndex, maxZIndex);
      this.portalStyles = {
        position: 'fixed',
        top: `${top}px`,
        left: `${Math.round(rect.left)}px`,
        minWidth: this.getPortalMinWidth(rect.width),
        maxHeight: `${maxHeight}px`,
        zIndex,
      };
      if (this.matchTriggerWidth) {
        this.portalStyles.width = `${Math.round(rect.width)}px`;
      }
    },
    ensurePanelInBody() {
      if (!this.shouldUsePortal()) return;
      const panel = this.$refs.panel;
      const host = this.getPortalHost();
      if (panel && host && panel.parentNode !== host) {
        host.appendChild(panel);
      }
    },
    restorePanelToRoot() {
      if (!this.shouldUsePortal()) return;
      const panel = this.$refs.panel;
      const root = this.$refs.root;
      if (panel && root && panel.parentNode !== root) {
        root.appendChild(panel);
      }
      this.portalStyles = null;
    },
    addPositionListeners() {
      if (!this.shouldUsePortal()) return;
      window.addEventListener('resize', this.syncPanelPosition);
      window.addEventListener('scroll', this.syncPanelPosition, true);
    },
    removePositionListeners() {
      // Always remove to avoid leaks if portal condition changes during lifecycle.
      window.removeEventListener('resize', this.syncPanelPosition);
      window.removeEventListener('scroll', this.syncPanelPosition, true);
    },
    getOptionId(index) {
      return `${this.dropdownId}-option-${index}`;
    },
    getFirstEnabledIndex() {
      return this.options.findIndex((option) => !option.disabled);
    },
    getNextEnabledIndex(currentIndex, step) {
      if (!this.options.length) return -1;
      let nextIndex = currentIndex;
      for (let i = 0; i < this.options.length; i += 1) {
        nextIndex =
          (nextIndex + step + this.options.length) % this.options.length;
        if (!this.options[nextIndex].disabled) {
          return nextIndex;
        }
      }
      return -1;
    },
    focusTrigger() {
      if (
        this.$refs.trigger &&
        typeof this.$refs.trigger.focus === 'function'
      ) {
        this.$refs.trigger.focus();
      }
    },
    scrollFocusedIntoView() {
      this.$nextTick(() => {
        const refs = this.$refs.optionNodes || [];
        const node = refs[this.focusedIndex];
        if (node && typeof node.scrollIntoView === 'function') {
          node.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    openDropdown(preferredIndex = null) {
      if (this.isTriggerDisabled || this.isOpen) return;
      registerOpenDropdown(this.dropdownId, () => this.forceClose());
      this.isOpen = true;
      if (preferredIndex == null) {
        const selectedOrFirst =
          this.selectedIndex >= 0
            ? this.selectedIndex
            : this.getFirstEnabledIndex();
        this.focusedIndex =
          selectedOrFirst >= 0 && !this.options[selectedOrFirst].disabled
            ? selectedOrFirst
            : this.getFirstEnabledIndex();
      } else {
        this.focusedIndex = preferredIndex;
      }
      this.scrollFocusedIntoView();
      this.addDocumentListeners();
      this.$nextTick(() => {
        this.ensurePanelInBody();
        this.syncPanelPosition();
        this.addPositionListeners();
      });
    },
    closeDropdown(returnFocus = false) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.focusedIndex = -1;
      this.removeDocumentListeners();
      this.removePositionListeners();
      this.restorePanelToRoot();
      unregisterOpenDropdown(this.dropdownId);
      if (returnFocus) {
        this.$nextTick(this.focusTrigger);
      }
    },
    forceClose() {
      this.closeDropdown();
    },
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    handleOutsidePointerDown(event) {
      if (!this.$refs.root) return;
      if (!this.containsEventTarget(event.target)) {
        this.closeDropdown();
      }
    },
    emitSelection(option) {
      const payload = {
        id: this.dropdownId,
        value: option.value,
        label: option.label,
      };
      this.$emit('input', option.value);
      this.$emit('change', option.value, payload);
      this.$el.dispatchEvent(
        new CustomEvent('dropdown:change', {
          detail: payload,
          bubbles: true,
        })
      );
    },
    selectOptionByIndex(index) {
      const option = this.options[index];
      if (!option || option.disabled) return;
      this.emitSelection(option);
      this.closeDropdown(true);
    },
    moveFocus(step) {
      const baseIndex =
        this.focusedIndex >= 0
          ? this.focusedIndex
          : this.selectedIndex >= 0
          ? this.selectedIndex
          : -1;
      const nextIndex = this.getNextEnabledIndex(baseIndex, step);
      if (nextIndex >= 0) {
        this.focusedIndex = nextIndex;
        this.scrollFocusedIntoView();
      }
    },
    // eslint-disable-next-line complexity
    onTriggerKeydown(event) {
      if (this.isTriggerDisabled) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown(this.getFirstEnabledIndex());
        } else {
          this.moveFocus(1);
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown(this.getFirstEnabledIndex());
        } else {
          this.moveFocus(-1);
        }
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        } else if (this.focusedIndex >= 0) {
          this.selectOptionByIndex(this.focusedIndex);
        }
      } else if (event.key === 'Escape') {
        if (this.isOpen) {
          event.preventDefault();
          this.closeDropdown(true);
        }
      } else if (event.key === 'Tab' && this.isOpen) {
        this.closeDropdown();
      }
    },
    // eslint-disable-next-line complexity
    onMenuKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveFocus(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveFocus(-1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (this.focusedIndex >= 0) {
          this.selectOptionByIndex(this.focusedIndex);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDropdown(true);
      } else if (event.key === 'Tab') {
        this.closeDropdown();
      }
    },
    onOptionMouseEnter(index) {
      if (this.options[index] && !this.options[index].disabled) {
        this.focusedIndex = index;
      }
    },
  },
};
</script>

<style scoped>
.sb-global-dropdown {
  position: relative;
  display: inline-block;
}

.sb-global-dropdown.is-full-width {
  width: 100%;
}

.sb-global-dropdown__trigger {
  width: 100%;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid var(--border2, var(--border));
  background: var(--sb-dd-surface, var(--surface));
  color: var(--sb-dd-text, var(--text));
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.sb-global-dropdown__trigger.size-24 {
  height: 24px;
  font-size: 12px;
}

.sb-global-dropdown__trigger.size-28 {
  height: 28px;
  font-size: 12px;
}

.sb-global-dropdown__trigger.size-32 {
  height: 32px;
  font-size: 12px;
}

.sb-global-dropdown__trigger.trigger-variant-admin-form {
  border-radius: 8px;
  height: 36px;
  font-size: 13px;
  border-color: var(--border2, #cbd5e1);
}

.sb-global-dropdown__trigger.trigger-variant-admin-form:focus-visible {
  outline: none;
  border-color: var(--blue, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

html.dark .sb-global-dropdown__trigger.trigger-variant-admin-form,
html:not(.light):not(.dark)
  .sb-global-dropdown__trigger.trigger-variant-admin-form {
  border-color: var(--border, #374151);
}

html.dark .sb-global-dropdown__trigger.trigger-variant-admin-form:focus-visible,
html:not(.light):not(.dark)
  .sb-global-dropdown__trigger.trigger-variant-admin-form:focus-visible {
  border-color: var(--blue, #60a5fa);
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.15);
}

.sb-global-dropdown__trigger.trigger-variant-legacy-underline {
  border: 0;
  border-bottom: 1px solid var(--sb-dd-border, var(--border));
  border-radius: 0;
  background: transparent;
  padding: 0 0 2px 0;
}

.sb-global-dropdown__trigger.trigger-variant-legacy-underline:hover:not(:disabled) {
  border-color: transparent;
  border-bottom-color: var(--sb-dd-border2, var(--border2));
}

.sb-global-dropdown__trigger.trigger-variant-legacy-underline:focus-visible {
  outline: none;
  box-shadow: none;
  border-bottom-color: var(--sb-dd-blue, var(--blue));
}

.sb-global-dropdown__trigger:hover:not(:disabled) {
  border-color: var(--sb-dd-border2, var(--border2));
}

.sb-global-dropdown__trigger:focus-visible {
  outline: 2px solid var(--sb-dd-blue, var(--blue));
  outline-offset: 2px;
}

.sb-global-dropdown__trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sb-global-dropdown__label {
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sb-global-dropdown__chevron {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--sb-dd-text3, var(--text3));
  transition: transform 0.2s;
  flex-shrink: 0;
}

.sb-global-dropdown.is-open .sb-global-dropdown__chevron {
  transform: rotate(180deg);
}

.sb-global-dropdown__panel {
  position: absolute;
  left: 0;
  top: calc(100% + 2px);
  margin: 0;
  padding: 4px 0;
  list-style: none;
  z-index: 300;
  border-radius: 8px;
  border: 1px solid var(--sb-dd-border, var(--border));
  background: var(--sb-dd-surface, var(--surface));
  box-shadow: var(--sb-dd-shadow);
  max-height: 280px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--sb-dd-border2, var(--border2))
    var(--sb-dd-surface, var(--surface));
}

.sb-global-dropdown__panel::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.sb-global-dropdown__panel::-webkit-scrollbar-track {
  background: var(--sb-dd-surface, var(--surface));
}

.sb-global-dropdown__panel::-webkit-scrollbar-thumb {
  background: var(--sb-dd-border2, var(--border2));
  border-radius: 999px;
}

.sb-global-dropdown__panel::-webkit-scrollbar-thumb:hover {
  background: var(--sb-dd-text3, var(--text3));
}

.sb-global-dropdown__option {
  margin: 1px 4px;
  border-radius: var(--sb-radius-sm, 6px);
  padding: 8px 14px;
  font-size: 13px;
  line-height: 1.35;
  color: var(--sb-dd-text, var(--text));
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sb-global-dropdown__option.is-focused {
  background: var(--sb-dd-surface2, var(--surface2));
}

.sb-global-dropdown__option.is-selected {
  background: var(--sb-dd-selected-bg, var(--selected-bg));
  color: var(--sb-dd-selected-text, var(--selected-text));
  font-weight: 500;
}

.sb-global-dropdown__option.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sb-global-dropdown__option.is-empty {
  text-align: left;
}
</style>
